import React, { useEffect, useMemo, useState } from "react";
import { Copy } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  PatchLog,
  PromptDiffToken,
  PromptLog,
  promptWordDiff,
  resolvePromptLogs,
} from "./promptDiff";
import { getFetcher } from "api/swr";
import { Hint } from "./Hint";

export type PromptVersion = {
  ban_words?: string[];
  base_ban_words?: string[];
  id: string;
  version: number;
  prompt: string;
  note?: string;
  historyFormat?: 2;
  logs?: (PromptLog | PatchLog)[];
  logCount?: number;
  basePrompt?: string;
  baseVersion?: number | null;
  updatedAt?: number;
  draft?: {
    schema: 1;
    formatRevision?: 2;
    baseVersion: number | null;
    basePrompt: string;
    updatedAt: number;
  } | null;
  previousVersion?: number | null;
};

export function BanWordsDiff({
  before = [],
  after = [],
}: {
  before?: string[];
  after?: string[];
}) {
  const added = after.filter((word) => !before.includes(word));
  const removed = before.filter((word) => !after.includes(word));
  if (!added.length && !removed.length) return null;
  return (
    <div
      aria-label="禁止标签变更"
      className="mb-3 space-y-1 rounded-lg border p-3 text-sm"
    >
      {added.length ? (
        <p className="text-emerald-700 dark:text-emerald-300">
          新增禁止：{added.join("、")}
        </p>
      ) : null}
      {removed.length ? (
        <p className="text-rose-700 dark:text-rose-300">
          取消禁止：{removed.join("、")}
        </p>
      ) : null}
    </div>
  );
}

export function WordDiff({ diff }: { diff: PromptDiffToken[] }) {
  if (!diff.some((token) => token.type !== "equal" && token.value))
    return (
      <p className="text-sm text-muted-foreground">提示词正文没有变化。</p>
    );
  return (
    <>
      <p className="mb-2 text-xs text-muted-foreground">
        绿色为新增，红色删除线为移除。
      </p>
      <div
        className="whitespace-pre-wrap break-words rounded-lg border bg-background p-3 font-mono text-xs leading-6"
        aria-label="词级差异"
      >
        {diff.map((token, index) =>
          token.type === "insert" ? (
            <ins
              key={index}
              className="rounded-sm bg-emerald-100 text-emerald-900 no-underline dark:bg-emerald-950 dark:text-emerald-200"
            >
              {token.value}
            </ins>
          ) : token.type === "delete" ? (
            <del
              key={index}
              className="rounded-sm bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-200"
            >
              {token.value}
            </del>
          ) : (
            <span key={index}>{token.value}</span>
          )
        )}
      </div>
    </>
  );
}

export function promptLogDebugText(log: PromptLog, index: number): string {
  return JSON.stringify(
    {
      format: "tag-prompt-lab-change/v1",
      logNumber: index + 1,
      source: log.source || null,
      createdAt: log.createdAt || null,
      module: log.module || null,
      request: log.request || null,
      description: log.description,
      ban_words_before: log.banWords?.before ?? null,
      ban_words_after: log.banWords?.after ?? null,
      beforePrompt: log.diff
        .filter((token) => token.type !== "insert")
        .map((token) => token.value)
        .join(""),
      afterPrompt: log.diff
        .filter((token) => token.type !== "delete")
        .map((token) => token.value)
        .join(""),
      diff: log.diff,
    },
    null,
    2
  );
}

export function PromptLogs({
  logs,
  basePrompt,
  finalPrompt,
}: {
  logs: (PatchLog | PromptLog)[];
  basePrompt?: string;
  finalPrompt?: string;
}) {
  const resolved = useMemo(() => {
    try {
      return {
        logs: resolvePromptLogs(logs, basePrompt, finalPrompt),
        error: "",
      };
    } catch (error) {
      return {
        logs: [],
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }, [logs, basePrompt, finalPrompt]);
  const { toast } = useToast();
  const [copyFallback, setCopyFallback] = useState("");
  const [copyKind, setCopyKind] = useState("调试日志");
  const copyLog = async (log: PromptLog, index: number) => {
    setCopyKind("调试日志");
    const text = promptLogDebugText(log, index);
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "调试日志已复制",
        description: "包含修改前后全文，可直接粘贴到对话中。",
      });
    } catch {
      setCopyFallback(text);
    }
  };
  const copyPrompt = async (log: PromptLog) => {
    const text = log.diff
      .filter((token) => token.type !== "delete")
      .map((token) => token.value)
      .join("");
    setCopyKind("提示词");
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "修改后的提示词已复制" });
    } catch {
      setCopyFallback(text);
    }
  };
  return (
    <div className="space-y-2">
      {resolved.error ? (
        <p role="alert" className="text-sm text-destructive">
          修改记录校验失败：{resolved.error}
        </p>
      ) : !resolved.logs.length ? (
        <p className="text-xs text-muted-foreground">暂无修改日志。</p>
      ) : (
        resolved.logs.map((log, index) => (
          <details className="rounded-lg border p-3" key={index}>
            <summary className="cursor-pointer whitespace-pre-line text-sm font-medium">
              {index + 1}. {log.description}
            </summary>
            <div className="mt-3">
              <div className="mb-2 flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void copyLog(log, index)}
                  aria-label={`复制第 ${index + 1} 条调试日志`}
                >
                  <Copy className="mr-2 h-3.5 w-3.5" />
                  复制调试日志
                </Button>
                <Hint content="复制这次修改后的完整提示词">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void copyPrompt(log)}
                    aria-label={`复制第 ${index + 1} 条修改后的提示词`}
                  >
                    <Copy className="mr-2 h-3.5 w-3.5" />
                    复制提示词
                  </Button>
                </Hint>
              </div>
              {log.request ? (
                <p className="mb-2 whitespace-pre-wrap text-xs text-muted-foreground">
                  修改要求：{log.request}
                </p>
              ) : null}
              {log.source ? (
                <p className="mb-2 text-xs text-muted-foreground">
                  {log.undoOf
                    ? "撤销修改"
                    : log.source === "chat"
                    ? `聊天修改 · ${log.module || "全文"}`
                    : "手动修改"}
                  {log.createdAt
                    ? ` · ${new Date(log.createdAt).toLocaleString("zh-CN")}`
                    : ""}
                </p>
              ) : null}
              {log.banWords ? (
                <BanWordsDiff
                  before={log.banWords.before}
                  after={log.banWords.after}
                />
              ) : null}
              {!log.banWords ||
              log.diff.some((token) => token.type !== "equal") ? (
                <WordDiff diff={log.diff} />
              ) : null}
            </div>
          </details>
        ))
      )}
      <Dialog
        open={Boolean(copyFallback)}
        onOpenChange={(open) => {
          if (!open) setCopyFallback("");
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>手动复制{copyKind}</DialogTitle>
            <DialogDescription>
              浏览器未允许自动复制。点击下方文本全选后复制，再粘贴到对话中。
            </DialogDescription>
          </DialogHeader>
          <Textarea
            aria-label={`${copyKind}内容`}
            readOnly
            value={copyFallback}
            className="h-[50vh] font-mono text-xs"
            onFocus={(event) => event.target.select()}
            onClick={(event) => event.currentTarget.select()}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PromptVersionHistory({
  versions,
  latestVersion,
  page,
  totalPages,
  onPageChange,
  loading,
  draftItem,
}: {
  versions: PromptVersion[];
  latestVersion?: number;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  draftItem?: React.ReactNode;
}) {
  const [selected, setSelected] = useState<PromptVersion | null>(null);
  const [view, setView] = useState<"diff" | "logs" | "prompt">("logs");
  const [detail, setDetail] = useState<PromptVersion | null>(null);
  const [comparison, setComparison] = useState<{
    beforePrompt: string;
    afterPrompt: string;
    beforeBanWords?: string[];
    afterBanWords?: string[];
    previousVersion: number | null;
  } | null>(null);
  const [detailError, setDetailError] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!selected || view === "prompt") return;
    let active = true;
    setDetail(null);
    setComparison(null);
    setDetailError("");
    setDetailLoading(true);
    getFetcher(
      `/tag-prompt-lab/prompts/${encodeURIComponent(selected.id)}/${
        view === "logs" ? "logs" : "comparison"
      }`
    )
      .then((response: any) => {
        if (!active) return;
        if (view === "logs") {
          if (!Array.isArray(response?.logs))
            throw new Error("修改记录响应无效");
          setDetail(response);
        } else {
          if (
            typeof response?.beforePrompt !== "string" ||
            typeof response?.afterPrompt !== "string"
          )
            throw new Error("版本对比响应无效");
          setComparison(response);
        }
      })
      .catch((error: unknown) => {
        if (active)
          setDetailError(
            error instanceof Error ? error.message : String(error)
          );
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selected, view, retry]);
  const comparisonDiff = useMemo(
    () =>
      comparison
        ? promptWordDiff(comparison.beforePrompt, comparison.afterPrompt)
        : [],
    [comparison]
  );
  const draftInfo = detail?.historyFormat === 2 ? detail : detail?.draft;
  return (
    <section
      className="flex min-h-0 flex-1 flex-col gap-2"
      aria-label="提示词版本列表"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">提示词版本</h4>
      </div>
      <div className="min-h-0 max-h-[432px] flex-1 space-y-2 overflow-y-auto lg:max-h-none">
        {draftItem}
        {versions.map((version) => (
          <button
            type="button"
            key={version.id}
            aria-label={`查看提示词 v${version.version}`}
            className="flex h-20 w-full flex-col justify-between rounded-lg border p-3 text-left hover:border-primary/50 hover:bg-muted/30"
            onClick={() => {
              setSelected(version);
              setView("logs");
            }}
          >
            <span className="flex items-center justify-between text-sm font-medium">
              v{version.version}
              {version.version === latestVersion ? (
                <Badge variant="secondary">最新发布</Badge>
              ) : null}
            </span>
            <span className="mt-1 block w-full truncate text-xs text-muted-foreground">
              {version.note || "查看版本"}
              {version.logCount !== undefined
                ? ` · ${version.logCount} 次修改`
                : ""}
            </span>
          </button>
        ))}
      </div>
      {!versions.length ? (
        <p className="text-xs text-muted-foreground">
          {loading ? "正在加载版本…" : "暂无发布版本"}
        </p>
      ) : null}
      {totalPages > 1 ? (
        <div className="flex shrink-0 items-center justify-end gap-3">
          <Button
            size="sm"
            variant="outline"
            disabled={loading || page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            上一页
          </Button>
          <span className="text-xs text-muted-foreground">
            {page} / {totalPages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={loading || page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            下一页
          </Button>
        </div>
      ) : null}
      <Dialog
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>提示词 v{selected?.version}</DialogTitle>
            <DialogDescription>
              只读查看，当前本地草稿保持不变。
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["diff", "版本对比"],
                ["logs", "修改日志"],
                ["prompt", "完整正文"],
              ] as const
            ).map(([value, label]) => (
              <Button
                key={value}
                size="sm"
                variant={view === value ? "default" : "outline"}
                onClick={() => setView(value)}
              >
                {label}
              </Button>
            ))}
          </div>
          <div className="min-h-0 space-y-3 overflow-auto">
            <p className="text-xs text-muted-foreground">
              禁止标签（历史记录，不影响全局配置）：{selected?.ban_words?.join("、") || "无"}
            </p>
            {view !== "prompt" && detailLoading ? (
              <p className="text-sm text-muted-foreground">正在加载…</p>
            ) : view !== "prompt" && detailError ? (
              <div role="alert" className="text-sm text-destructive">
                {detailError}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRetry((value) => value + 1)}
                >
                  重试
                </Button>
              </div>
            ) : view === "diff" ? (
              <>
                <p className="text-xs text-muted-foreground">
                  {comparison?.previousVersion
                    ? `v${comparison.previousVersion}`
                    : "空白起点"}{" "}
                  → v{selected?.version} · 绿色新增，红色删除
                </p>
                <BanWordsDiff
                  before={comparison?.beforeBanWords}
                  after={comparison?.afterBanWords}
                />
                <WordDiff diff={comparisonDiff} />
              </>
            ) : view === "logs" ? (
              <>
                {draftInfo ? (
                  <div className="space-y-2 rounded-lg bg-muted/30 p-3 text-xs text-muted-foreground">
                    <p>
                      草稿基于{" "}
                      {draftInfo.baseVersion
                        ? `v${draftInfo.baseVersion}`
                        : "内置基准"}{" "}
                      · 最后修改{" "}
                      {new Date(draftInfo.updatedAt || 0).toLocaleString(
                        "zh-CN"
                      )}
                    </p>
                    <details>
                      <summary className="cursor-pointer">查看修改起点</summary>
                      <pre className="mt-2 whitespace-pre-wrap break-words leading-6">
                        {draftInfo.basePrompt}
                      </pre>
                    </details>
                  </div>
                ) : null}
                <PromptLogs
                  logs={detail?.logs || []}
                  basePrompt={
                    detail?.historyFormat === 2 ? detail.basePrompt : undefined
                  }
                  finalPrompt={
                    detail?.historyFormat === 2 ? detail.prompt : undefined
                  }
                />
              </>
            ) : (
              <pre className="whitespace-pre-wrap break-words rounded-lg bg-muted/30 p-4 text-xs leading-6">
                {selected?.prompt}
              </pre>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
